var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { makeAutoObservable, runInAction } from 'mobx';
import _ from 'lodash';
import { _legacyNtsService } from 'ziphy-web-shared/basic/api';
import { _LEGACY_NotesSectionsKeys, } from 'ziphy-web-shared/basic/entities/_LEGACY_/notes';
import { getEncounterStatus } from 'ziphy-web-shared/basic/entities/encounters';
import { withFullName } from '@modules/_LEGACY_/fhir/fhir.helpers';
import { ADDENDUM_DATA } from '@modules/_LEGACY_/notes/sections/addendum/Addendum.config';
export class LegacyNotesStore {
    constructor() {
        this.appointment = null;
        this.encounter = null;
        this.patient = null;
        this.isStartProvider = null;
        this.sidebar = null;
        this.signature = null;
        this.addendum = [];
        makeAutoObservable(this);
    }
    clear(full) {
        if (full) {
            this.appointment = null;
            this.encounter = null;
            this.patient = null;
            this.isStartProvider = null;
        }
        this.signature = null;
        this.addendum = [];
    }
    // Computed
    get encounterStatus() {
        return getEncounterStatus(this.encounter);
    }
    get isEditable() {
        return !this.isLocked && this.encounterStatus.isStarted && !this.encounterStatus.isCancelled;
    }
    get isSigned() {
        var _a;
        return Boolean((_a = this.signature) === null || _a === void 0 ? void 0 : _a.author);
    }
    get isLocked() {
        return this.isSigned;
    }
    get beforeSignErrors() {
        let result = new Set();
        if (this.isLocked)
            result.add('is_locked');
        if (!this.isStartProvider)
            result.add('can_not_signing');
        if (this.encounterStatus.isCancelled)
            result.add('encounter_cancelled');
        if (!this.encounterStatus.isStarted)
            result.add('encounter_not_started');
        if (this.encounterStatus.isInProgress)
            result.add('encounter_not_finished');
        return result;
    }
    // Mutations
    UPDATE_CONTEXT({ encounter, appointment, patient, isStartProvider }) {
        if (encounter) {
            this.encounter = this.encounter ? Object.assign(Object.assign({}, this.encounter), encounter) : encounter;
        }
        if (appointment) {
            this.appointment = this.appointment ? Object.assign(Object.assign({}, this.appointment), appointment) : appointment;
        }
        if (patient) {
            this.patient = this.patient ? Object.assign(Object.assign({}, this.patient), patient) : patient;
        }
        this.isStartProvider = isStartProvider;
    }
    SET_SIDEBAR(value) {
        this.sidebar = value;
    }
    // Actions
    loadAddendum() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.signature.provenanceRef)
                return;
            const response = yield _legacyNtsService.readSignedNotes({
                encounterId: this.encounter.id,
                provenanceRef: this.signature.provenanceRef,
                sections: [_LEGACY_NotesSectionsKeys.ADDENDUM],
            });
            const resources = (_b = (_a = response.prepared) === null || _a === void 0 ? void 0 : _a.addendum) === null || _b === void 0 ? void 0 : _b.resources;
            if (!(resources === null || resources === void 0 ? void 0 : resources.length))
                return;
            runInAction(() => {
                const data = ADDENDUM_DATA.addendum.onRead(resources);
                const result = data.map((item) => {
                    return { text: item.value.text, time: item.value.time, author: item.value.author.name };
                });
                this.addendum = _.orderBy(result, ['time'], ['asc']);
            });
        });
    }
    loadSignature() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield _legacyNtsService.listSignatures({ encounterId: this.encounter.id });
            if (response.error)
                return;
            runInAction(() => {
                var _a, _b;
                const data = (_b = (_a = response.prepared) === null || _a === void 0 ? void 0 : _a.signatures) === null || _b === void 0 ? void 0 : _b[0];
                if (!data)
                    return;
                const createAuthor = (author, time = '') => {
                    if (!author)
                        return null;
                    const name = withFullName(author);
                    return {
                        name: [name.firstName, name.lastName].join(' '),
                        roleZid: author.roleZid,
                        npi: author.npi,
                        signature: '',
                        time: time,
                    };
                };
                this.signature = {
                    provenanceRef: data.provenanceRef,
                    rendered: data.document,
                    date: data.date,
                    author: createAuthor(data.author, data.date),
                    coauthor: createAuthor(data.coauthor),
                };
            });
        });
    }
    open({ appointment, encounter, patient, isStartProvider }) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!(encounter === null || encounter === void 0 ? void 0 : encounter.id) || encounter.id === ((_a = this.encounter) === null || _a === void 0 ? void 0 : _a.id))
                return;
            this.clear(true);
            this.UPDATE_CONTEXT({ appointment, encounter, patient, isStartProvider });
            yield this.loadSignature();
            if (this.signature)
                yield this.loadAddendum();
        });
    }
    sign(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (params.addendumText) {
                yield _legacyNtsService.saveAddendum({
                    encounterId: this.encounter.id,
                    addendum: {
                        resources: [
                            ADDENDUM_DATA.addendum.onSave({ value: params.addendumText, customParams: {} }),
                        ],
                    },
                });
            }
            const response = yield _legacyNtsService.signNotes({
                encounterId: this.encounter.id,
                signature: params.signature,
                clientNotification: params.clientNotification,
            });
            if (!response.error) {
                yield this.loadSignature();
                if (params.addendumText) {
                    yield this.loadAddendum();
                }
            }
            return response;
        });
    }
}
export const $legacyNotes = new LegacyNotesStore();
/*
 * Other
 */
// $loader.subscribe(globalLoaderHandlers.LOGOUT, () => {
// })
